import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Bed, Bath, Maximize2, Building, X, ZoomIn, ZoomOut, RotateCcw, Expand } from 'lucide-react';

export const InteractiveMap = ({ mapImage, units, onUnitClick }) => {
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [hoveredUnit, setHoveredUnit] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Zoom/pan state - only used in fullscreen mode
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMobileView, setIsMobileView] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => setIsMobileView(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate marker size based on number of units
  const getMarkerSize = (isMobile = false) => {
    if (isMobile) {
      // Responsive smaller markers for mobile only
      const unitCount = units.length;
      if (unitCount > 100) return 4;
      if (unitCount > 50) return 5;
      if (unitCount > 20) return 6;
      return 8;
    } else {
      // Desktop: always use original size (12px)
      return 12;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return <span className="status-available px-3 py-1 text-[10px] uppercase tracking-widest font-light">Available</span>;
      case 'sold':
        return <span className="status-sold px-3 py-1 text-[10px] uppercase tracking-widest font-light">Sold</span>;
      case 'pending':
        return <span className="status-pending px-3 py-1 text-[10px] uppercase tracking-widest font-light">Pending</span>;
      default:
        return null;
    }
  };

  const handleUnitClick = (unit, e) => {
    e.stopPropagation();
    if (onUnitClick) {
      onUnitClick(unit);
    } else {
      setSelectedUnit(unit);
    }
  };

  // Reset zoom when entering/exiting fullscreen
  const openFullscreen = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsFullscreen(true);
  };

  const closeFullscreen = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsFullscreen(false);
  };

  // Zoom functions - smaller increments for smoother zoom
  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 1));
  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // Mouse wheel zoom - smoother with smaller increments
  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setScale(prev => {
      const newScale = prev + delta;
      if (newScale < 1) return 1;
      if (newScale > 3) return 3;
      return newScale;
    });
  };

  // Pan functions
  const handleMouseDown = (e) => {
    if (e.target.closest('[data-marker]')) return;
    if (scale <= 1) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    const maxPan = (scale - 1) * 200;
    setPosition({
      x: Math.max(-maxPan, Math.min(maxPan, newX)),
      y: Math.max(-maxPan, Math.min(maxPan, newY))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (scale <= 1) {
      setPosition({ x: 0, y: 0 });
    }
  }, [scale]);

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  // Render unit markers with responsive sizing
  const renderMarkers = (isMobile = false, isFullscreenView = false) => {
    const baseSize = getMarkerSize(isMobile);
    // In fullscreen, markers can be slightly larger
    const size = isFullscreenView ? Math.max(baseSize, 8) : baseSize;
    
    return (
      <>
        {units.map((unit) => (
          <button
            key={unit.id}
            data-marker
            onClick={(e) => handleUnitClick(unit, e)}
            onMouseEnter={() => !isMobile && setHoveredUnit(unit.id)}
            onMouseLeave={() => setHoveredUnit(null)}
            className={`absolute rounded-full cursor-pointer z-10
              transition-transform duration-200
              ${!isMobile ? 'hover:scale-150' : ''}
              ${unit.status === 'available' ? 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.5)]' : ''}
              ${unit.status === 'sold' ? 'bg-red-500 opacity-60' : ''}
              ${unit.status === 'pending' ? 'bg-orange-500 shadow-[0_0_6px_rgba(251,146,60,0.5)]' : ''}`}
            style={{
              left: `${unit.position_x}%`,
              top: `${unit.position_y}%`,
              width: `${size}px`,
              height: `${size}px`,
              transform: 'translate(-50%, -50%)',
              minWidth: `${size}px`,
              minHeight: `${size}px`,
            }}
            data-testid={`unit-marker-${unit.id}`}
          >
            {/* Tooltip - only on desktop */}
            {!isMobile && hoveredUnit === unit.id && (
              <div 
                className="absolute bottom-full left-1/2 mb-2 bg-black/90 border border-white/20 px-3 py-2 whitespace-nowrap z-20"
                style={{ transform: 'translateX(-50%)' }}
              >
                <p className="text-white text-[10px] font-medium">{unit.unit_number}</p>
                <p className="text-white/60 text-[9px] font-light">{unit.price}</p>
              </div>
            )}
          </button>
        ))}
      </>
    );
  };

  const mapSrc = mapImage || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200&q=80';

  return (
    <div className="space-y-4" data-testid="interactive-map">
      {/* Normal View - Static map with fullscreen button */}
      <div className="relative">
        {/* Map Container - maintains aspect ratio and positions markers relative to image */}
        <div className="relative w-full aspect-[16/10] overflow-hidden border border-white/10 bg-black">
          <img
            src={mapSrc}
            alt="Project Map"
            className="absolute inset-0 w-full h-full object-cover select-none"
            draggable={false}
            data-testid="map-image"
          />
          
          {/* Markers overlay - Desktop version */}
          <div className="absolute inset-0 hidden sm:block">
            {renderMarkers(false, false)}
          </div>
          
          {/* Markers overlay - Mobile version (smaller markers) */}
          <div className="absolute inset-0 sm:hidden">
            {renderMarkers(true, false)}
          </div>
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
        </div>
        
        {/* Fullscreen Button */}
        <button
          onClick={openFullscreen}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 p-2 sm:p-3 bg-black/60 hover:bg-black/80 text-white transition-colors z-20"
          title="View Fullscreen"
          data-testid="fullscreen-btn"
        >
          <Expand className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>

      {/* Legend - Responsive */}
      <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 py-2 sm:py-4">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.5)]" />
          <span className="text-white/60 text-[10px] sm:text-xs uppercase tracking-[0.1em] sm:tracking-[0.15em] font-light">Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-orange-500 shadow-[0_0_6px_rgba(251,146,60,0.5)]" />
          <span className="text-white/60 text-[10px] sm:text-xs uppercase tracking-[0.1em] sm:tracking-[0.15em] font-light">Pending</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-500 opacity-60" />
          <span className="text-white/60 text-[10px] sm:text-xs uppercase tracking-[0.1em] sm:tracking-[0.15em] font-light">Sold</span>
        </div>
        <button
          onClick={openFullscreen}
          className="flex items-center gap-2 text-white/40 hover:text-white transition-colors"
        >
          <Expand className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.1em] sm:tracking-[0.15em] font-light">Fullscreen</span>
        </button>
      </div>

      {/* Unit count info */}
      <div className="text-center">
        <span className="text-white/30 text-[10px] sm:text-xs font-light">
          {units.length} units total
        </span>
      </div>

      {/* Fullscreen Map Dialog */}
      <Dialog open={isFullscreen} onOpenChange={closeFullscreen}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 bg-black border-white/10 overflow-hidden" data-testid="fullscreen-map-dialog">
          {/* Header with zoom controls */}
          <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between p-2 sm:p-4 bg-gradient-to-b from-black via-black/80 to-transparent">
            <div className="flex items-center gap-1 sm:gap-2 bg-black/60 p-1">
              <button
                onClick={zoomOut}
                disabled={scale <= 1}
                className="p-1.5 sm:p-2 hover:bg-white/10 text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Zoom Out"
              >
                <ZoomOut className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
              <span className="text-white text-[10px] sm:text-xs font-light px-2 sm:px-3 min-w-[45px] sm:min-w-[60px] text-center">{Math.round(scale * 100)}%</span>
              <button
                onClick={zoomIn}
                disabled={scale >= 3}
                className="p-1.5 sm:p-2 hover:bg-white/10 text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Zoom In"
              >
                <ZoomIn className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
              <button
                onClick={resetZoom}
                disabled={scale === 1 && position.x === 0 && position.y === 0}
                className="p-1.5 sm:p-2 hover:bg-white/10 text-white transition-colors ml-1 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Reset View"
              >
                <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>

            <p className="text-white/50 text-[10px] sm:text-xs font-light hidden md:block">
              {scale > 1 ? 'Drag to pan • Scroll to zoom • Click marker for details' : 'Scroll or use buttons to zoom • Click marker for details'}
            </p>

            <button
              onClick={closeFullscreen}
              className="p-1.5 sm:p-2 bg-black/60 hover:bg-white/10 text-white transition-colors"
              title="Close Fullscreen"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>

          {/* Fullscreen Map - Use same aspect ratio as normal view */}
          <div 
            className="w-full h-full flex items-center justify-center pt-12 sm:pt-16 pb-12 sm:pb-16 overflow-hidden"
            style={{ cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
          >
            {/* Zoomable wrapper */}
            <div
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                transformOrigin: 'center center',
              }}
            >
              {/* Container that maintains 16:10 aspect ratio - SAME as normal view */}
              <div 
                className="relative bg-black"
                style={{
                  width: 'min(85vw, calc((90vh - 128px) * 1.6))',
                  aspectRatio: '16 / 10',
                }}
              >
                {/* Image fills container completely - same as normal view */}
                <img
                  src={mapSrc}
                  alt="Project Map"
                  className="absolute inset-0 w-full h-full object-cover select-none"
                  draggable={false}
                />
                
                {/* Markers overlay in fullscreen - use mobile sizing if on mobile */}
                <div className="absolute inset-0">
                  {renderMarkers(isMobileView, true)}
                </div>
              </div>
            </div>
          </div>

          {/* Footer Legend */}
          <div className="absolute bottom-0 left-0 right-0 z-30 flex items-center justify-center gap-4 sm:gap-8 p-2 sm:p-4 bg-gradient-to-t from-black via-black/80 to-transparent">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.5)]" />
              <span className="text-white/60 text-[10px] sm:text-xs uppercase tracking-[0.1em] sm:tracking-[0.15em] font-light">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-orange-500 shadow-[0_0_6px_rgba(251,146,60,0.5)]" />
              <span className="text-white/60 text-[10px] sm:text-xs uppercase tracking-[0.1em] sm:tracking-[0.15em] font-light">Pending</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-500 opacity-60" />
              <span className="text-white/60 text-[10px] sm:text-xs uppercase tracking-[0.1em] sm:tracking-[0.15em] font-light">Sold</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unit Detail Modal */}
      <Dialog open={!!selectedUnit} onOpenChange={() => setSelectedUnit(null)}>
        <DialogContent className="glass-light max-w-xl border-white/10 p-0 overflow-hidden" data-testid="unit-detail-modal">
          {selectedUnit && (
            <>
              <button
                onClick={() => setSelectedUnit(null)}
                className="absolute top-4 right-4 z-30 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-black/70 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              <div className="relative h-48 bg-[#0a0a0a]">
                <img
                  src={selectedUnit.design_image || selectedUnit.view_image || 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'}
                  alt={selectedUnit.unit_number}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
              </div>

              <div className="p-6 sm:p-8 space-y-4 sm:space-y-6 bg-[#0a0a0a]">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight mb-2">
                      {selectedUnit.unit_number}
                    </h3>
                    {selectedUnit.floor && (
                      <p className="text-xs uppercase tracking-[0.15em] text-white/40 font-light">
                        {selectedUnit.floor}
                      </p>
                    )}
                  </div>
                  {getStatusBadge(selectedUnit.status)}
                </div>

                <div className="border-t border-b border-white/10 py-4 sm:py-6">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 font-light">Price</p>
                  <p className="text-2xl sm:text-3xl font-light text-white">{selectedUnit.price}</p>
                </div>

                <div className="grid grid-cols-4 gap-2 sm:gap-4">
                  <div className="text-center p-2 sm:p-4 border border-white/10">
                    <Bed className="w-4 h-4 sm:w-5 sm:h-5 text-white/40 mx-auto mb-1 sm:mb-2" />
                    <p className="text-white text-base sm:text-lg font-light">{selectedUnit.bedrooms || 0}</p>
                    <p className="text-[8px] sm:text-[9px] uppercase tracking-[0.1em] text-white/40 font-light">Beds</p>
                  </div>
                  <div className="text-center p-2 sm:p-4 border border-white/10">
                    <Bath className="w-4 h-4 sm:w-5 sm:h-5 text-white/40 mx-auto mb-1 sm:mb-2" />
                    <p className="text-white text-base sm:text-lg font-light">{selectedUnit.bathrooms || 0}</p>
                    <p className="text-[8px] sm:text-[9px] uppercase tracking-[0.1em] text-white/40 font-light">Baths</p>
                  </div>
                  <div className="text-center p-2 sm:p-4 border border-white/10">
                    <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5 text-white/40 mx-auto mb-1 sm:mb-2" />
                    <p className="text-white text-xs sm:text-sm font-light">{selectedUnit.area || 'N/A'}</p>
                    <p className="text-[8px] sm:text-[9px] uppercase tracking-[0.1em] text-white/40 font-light">Area</p>
                  </div>
                  <div className="text-center p-2 sm:p-4 border border-white/10">
                    <Building className="w-4 h-4 sm:w-5 sm:h-5 text-white/40 mx-auto mb-1 sm:mb-2" />
                    <p className="text-white text-xs sm:text-sm font-light">{selectedUnit.floor?.split(' ')[0] || 'N/A'}</p>
                    <p className="text-[8px] sm:text-[9px] uppercase tracking-[0.1em] text-white/40 font-light">Floor</p>
                  </div>
                </div>

                {selectedUnit.description && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3 font-light">Description</p>
                    <p className="text-white/60 text-sm font-light leading-relaxed">{selectedUnit.description}</p>
                  </div>
                )}

                {selectedUnit.view_image && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3 font-light">View</p>
                    <div className="overflow-hidden border border-white/10">
                      <img
                        src={selectedUnit.view_image}
                        alt="Unit View"
                        className="w-full h-40 object-cover"
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
